/**
 * WordPress Dependencies
 */
import { get } from "lodash";
import { __ } from "@wordpress/i18n";
import { shuffle, gallery } from "@wordpress/icons";
import { addFilter } from "@wordpress/hooks";
import { useEffect } from "@wordpress/element";
import { assign, isEmpty, has, isEqual } from "lodash";
import { createHigherOrderComponent } from "@wordpress/compose";
import { ToolbarGroup, ToolbarButton } from "@wordpress/components";

import {
	BlockControls,
	__experimentalGetGapCSSValue as getGapCSSValue,
} from "@wordpress/block-editor";
/**
 * Custom Imports
 */
import "./editor.scss";
import "./style.scss";
import { shuffleInnerBlocks } from "./utils";

/** @typedef {import('@wordpress/blocks').WPBlockVariation} WPBlockVariation */

/**
 * Template option choices for predefined columns layouts.
 *
 * @type {WPBlockVariation[]}
 */

wp.blocks.registerBlockVariation("core/gallery", {
	name: "core/gallery",
	title: __("Gallery", "ghub-masonry-ext"),
	description: __(
		"Display multiple images in a rich gallery.",
		"ghub-masonry-ext"
	),
	icon: gallery,
	isDefault: true,
	category: "media",
	attributes: {
		ghubVariation: "default",
		className: "",
	},
	scope: ["inserter", "transform"],
	isActive: (blockAttributes, variationAttributes) =>
		isEqual(blockAttributes.ghubVariation, variationAttributes.ghubVariation),
});
wp.blocks.registerBlockVariation("core/gallery", {
	name: "ghub-masonry-gallery",
	title: __("Masonry Gallery", "ghub-masonry-ext"),
	description: __(
		"Display multiple images in a masonry gallery.",
		"ghub-masonry-ext"
	),
	category: "ghub-products",
	keywords: ["masonry", "gallery"],
	icon: (
		<svg
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			style={{ fill: "none" }}
		>
			<path
				d="M3 3H10V12H3V3ZM14 3H21V8H14V3ZM14 12H21V21H14V12ZM3 16H10V21H3V16Z"
				stroke="currentColor"
				stroke-width="2"
				stroke-linecap="round"
				stroke-linejoin="round"
			/>
		</svg>
	),
	attributes: {
		ghubVariation: "masonry-gallery",
		className: "is-style-masonry",
		ghubVerticalGap: "20px",
		ghubHorizontalGap: "20px",
	},
	scope: ["inserter", "transform"],
	isActive: (blockAttributes, variationAttributes) =>
		isEqual(blockAttributes.ghubVariation, variationAttributes.ghubVariation),
});
/**
 * Add new attributes to the block
 */
const addAttributes = (props) => {
	const { attributes } = props;
	if (isEmpty(attributes) || props.name !== "core/gallery") {
		return props;
	}

	props.attributes = assign(
		props.attributes,
		{
			...attributes,
			ghubVariation: {
				type: "string",
				default: "default",
			},
			ghubVerticalGap: {
				type: "string",
				default: "",
			},
			ghubHorizontalGap: {
				type: "string",
				default: "",
			},
		},
		{}
	);

	// props.supports = {
	// 	...props.supports,
	// 	spacing: {
	// 		...get(props, "supports.spacing", {}),
	// 		blockGap: ["horizontal", "vertical"],
	// 	},
	// };

	// console.log("Updated: ", props);

	return props;
};

const withAdvanceControls = createHigherOrderComponent((BlockEdit) => {
	return (props) => {
		const { attributes, setAttributes, name } = props;

		if (
			name !== "core/gallery" ||
			(name === "core/gallery" &&
				attributes?.ghubVariation !== "masonry-gallery")
		) {
			return <BlockEdit {...props} />;
		}

		if (
			has(attributes, "style") &&
			has(attributes?.style, "spacing") &&
			has(attributes?.style?.spacing, "blockGap")
		) {
			useEffect(() => {
				setAttributes({
					ghubVerticalGap: getGapCSSValue(
						attributes?.style?.spacing?.blockGap?.top
					),
					ghubHorizontalGap: getGapCSSValue(
						attributes?.style?.spacing?.blockGap?.left
					),
				});
			}, [attributes?.style?.spacing?.blockGap]);
		}
		return (
			<>
				<BlockEdit {...props} />

				<BlockControls>
					<ToolbarGroup>
						<ToolbarButton
							label={__("Shuffle Images", "ghub-masonry-ext")}
							icon={shuffle}
							onClick={() => {
								shuffleInnerBlocks(props.clientId);
							}}
						/>
					</ToolbarGroup>
				</BlockControls>
			</>
		);
	};
}, "withAdvanceControls");

const withBlockWrapper = createHigherOrderComponent((BlockListBlock) => {
	return (props) => {
		const { attributes } = props;
		if (
			props.name !== "core/gallery" ||
			(props.name === "core/gallery" &&
				attributes?.ghubVariation !== "masonry-gallery")
		) {
			return <BlockListBlock {...props} />;
		}
		const wrapperProps = {
			...props.wrapperProps,
			style: {
				"--ghub--gallery-column-gap": getGapCSSValue(
					attributes?.ghubHorizontalGap
				),
				"--ghub--gallery-row-gap": getGapCSSValue(attributes?.ghubVerticalGap),
			},
		};

		return <BlockListBlock {...props} wrapperProps={wrapperProps} />;
	};
}, "withBlockWrapper");

addFilter(
	"editor.BlockEdit",
	"ghub-masonry-ext/with-advance-controls",
	withAdvanceControls
);
addFilter(
	"blocks.registerBlockType",
	"ghub-masonry-ext/add-attributes",
	addAttributes
);
addFilter(
	"editor.BlockListBlock",
	"ghub-masonry-ext/with-block-wrapper",
	withBlockWrapper
);
